import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { REPORT_ENTITY_LABELS, REPORT_FREQUENCY_LABELS, type Report } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { ReportFormModal } from './ReportFormModal.js';
import { ReportRunModal } from './ReportRunModal.js';
import { useReports, useRunDueReports, useScheduleHealth, useSendReport } from './api.js';

/** Short local date-time, e.g. "Jun 25, 8:00 AM". */
function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function lastRunBadge(report: Report): JSX.Element {
  if (report.lastRunStatus === 'ok') return <Badge tone="green">Last run OK</Badge>;
  if (report.lastRunStatus === 'failed') return <Badge tone="red">Last run failed</Badge>;
  if (report.lastRunStatus === 'skipped') return <Badge tone="amber">Last run skipped</Badge>;
  return <Badge tone="gray">Never run</Badge>;
}

export function ReportsPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Report | null>(null);
  const [runOpen, setRunOpen] = useState(false);
  const [running, setRunning] = useState<Report | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useReports();
  const reports = query.data ?? [];
  const health = useScheduleHealth(isAdmin);
  const sendReport = useSendReport();
  const runDue = useRunDueReports();

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (report: Report): void => {
    setEditing(report);
    setFormOpen(true);
  };

  const openRun = (report: Report): void => {
    setRunning(report);
    setRunOpen(true);
  };

  const handleSend = (report: Report): void => {
    setActionError(null);
    setSendingId(report.id);
    sendReport.mutate(report.id, {
      onError: (err) =>
        setActionError(err instanceof Error ? err.message : `Could not send "${report.name}".`),
      onSettled: () => setSendingId(null),
    });
  };

  const handleRunDue = (): void => {
    setActionError(null);
    runDue.mutate(undefined, {
      onError: (err) =>
        setActionError(err instanceof Error ? err.message : 'Could not run due reports.'),
      onSuccess: () => void health.refetch(),
    });
  };

  const stalled = Boolean(health.data?.stalled);

  return (
    <div>
      <PageHeader
        title="Reports"
        description="Run saved reports on demand and download their results."
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate('/reports/bonus')}>
              <Icon name="reports" className="h-4 w-4" />
              Monthly Team Bonus
            </Button>
            {isAdmin ? (
              <Button onClick={openCreate}>
                <Icon name="reports" className="h-4 w-4" />
                New report
              </Button>
            ) : null}
          </>
        }
      />

      {isAdmin && stalled ? (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold">Scheduled delivery may be stalled</p>
              <p className="mt-0.5 text-amber-800">
                {health.data?.lastRunDueAt
                  ? `The automated scheduler last ran ${formatDateTime(
                      health.data.lastRunDueAt,
                    )} (${health.data.ageMinutes ?? '?'} min ago). Scheduled emails may not be going out.`
                  : 'The automated scheduler has not run yet. Scheduled emails may not be going out.'}
                {health.data && health.data.overdueReports.length > 0
                  ? ` ${health.data.overdueReports.length} report(s) are overdue.`
                  : ''}
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleRunDue}
              disabled={runDue.isPending}
            >
              {runDue.isPending ? 'Running…' : 'Run all due now'}
            </Button>
          </div>
        </div>
      ) : null}

      {actionError ? (
        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {actionError}
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Data source</th>
              <th className="px-4 py-3 font-medium">Schedule</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={5}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={reports.length === 0}
              emptyTitle="No reports yet"
              emptyDescription={
                isAdmin
                  ? 'Create your first report to get started.'
                  : 'No reports have been created yet.'
              }
              emptyAction={isAdmin ? <Button onClick={openCreate}>New report</Button> : undefined}
            >
              {reports.map((report) => {
                const scheduled = report.enabled && report.frequency !== 'manual';
                return (
                  <tr key={report.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{report.name}</div>
                      {report.description ? (
                        <div className="text-xs text-slate-500">{report.description}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {report.entity ? REPORT_ENTITY_LABELS[report.entity] : 'Advanced (SQL)'}
                    </td>
                    <td className="px-4 py-3">
                      {scheduled ? (
                        <Badge tone="blue">{REPORT_FREQUENCY_LABELS[report.frequency]}</Badge>
                      ) : (
                        <Badge tone="gray">Manual</Badge>
                      )}
                      {scheduled ? (
                        <div className="mt-1 text-xs text-slate-500">
                          Next: {formatDateTime(report.nextRunAt)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {lastRunBadge(report)}
                      <div className="mt-1 text-xs text-slate-500">
                        Last: {formatDateTime(report.lastRunAt)}
                      </div>
                      {report.lastRunStatus === 'failed' && report.lastRunError ? (
                        <div
                          className="mt-0.5 max-w-[20rem] truncate text-xs text-red-600"
                          title={report.lastRunError}
                        >
                          {report.lastRunError}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={() => openRun(report)}>
                          Run
                        </Button>
                        {isAdmin ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleSend(report)}
                            disabled={sendingId === report.id}
                          >
                            {sendingId === report.id ? 'Sending…' : 'Send now'}
                          </Button>
                        ) : null}
                        {isAdmin ? (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(report)}>
                            Edit
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </DataState>
          </tbody>
        </table>
      </div>

      {isAdmin ? (
        <ReportFormModal isOpen={formOpen} report={editing} onClose={() => setFormOpen(false)} />
      ) : null}
      <ReportRunModal isOpen={runOpen} report={running} onClose={() => setRunOpen(false)} />
    </div>
  );
}
